import { useEffect, useMemo, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { CMS_SECTIONS, SECTION_MAP } from "@/lib/cms-schema";
import { FieldRenderer, validateForm, applyDefaults, prepareSubmit } from "@/components/vr/CmsField";
import { BulkImportCsv } from "@/components/vr/BulkImportCsv";
import { FileDropzone } from "@/components/vr/FileDropzone";
import { toast, Toaster } from "sonner";
import * as LucideIcons from "lucide-react";
import {
  LogOut, LayoutDashboard, Settings as SettingsIcon, ShoppingBag, MessageCircle,
  Home, ChartBar, Search as SearchIcon, Pencil, Trash2, Plus, X, Save, AlertTriangle,
  Upload,
} from "lucide-react";

/* ================= LOGIN ================= */
export function AdminLogin() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const nav = useNavigate();
  useEffect(() => { document.title = "Admin Login | VR Classes"; }, []);
  if (user && user.role === "admin") return <Navigate to="/admin" replace />;
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try { const u = await login(email, pw); if (u.role === "admin") nav("/admin"); else setErr("Access denied"); }
    catch (e) { setErr(formatApiError(e)); } finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <form onSubmit={submit} className="vr-card p-8 w-full max-w-md">
        <div className="w-12 h-12 rounded-lg bg-[#1E3A8A] text-white flex items-center justify-center font-black text-xl mb-4">VR</div>
        <h1 className="font-display font-extrabold text-2xl text-[#0F172A]">Admin Login</h1>
        <p className="text-sm text-slate-500 mt-1">VR Classes management</p>
        <div className="mt-6 space-y-3">
          <input required type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full h-12 px-4 border border-slate-200 rounded-lg" data-testid="admin-email" />
          <input required type="password" placeholder="Password" value={pw} onChange={(e) => setPw(e.target.value)} className="w-full h-12 px-4 border border-slate-200 rounded-lg" data-testid="admin-password" />
          {err && <div className="text-sm text-red-600" data-testid="admin-error">{err}</div>}
          <button disabled={loading} className="vr-btn-primary w-full" data-testid="admin-login-btn">{loading ? "Logging in..." : "Login"}</button>
        </div>
      </form>
    </div>
  );
}

/* ================= NAV ================= */
const NAV = [
  { path: "overview", label: "Overview", icon: LayoutDashboard },
  ...CMS_SECTIONS.map((s) => ({ path: s.key, label: s.label, icon: LucideIcons[s.icon] || LayoutDashboard, cms: true })),
  { path: "homepage", label: "Homepage Content", icon: Home },
  { path: "statistics", label: "Statistics", icon: ChartBar },
  { path: "seo", label: "SEO Settings", icon: SearchIcon },
  { path: "settings", label: "Site Settings", icon: SettingsIcon },
  { path: "orders", label: "Orders", icon: ShoppingBag },
  { path: "contacts", label: "Contacts", icon: MessageCircle },
];

/* ================= APP SHELL ================= */
export function AdminApp() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const nav = useNavigate();
  useEffect(() => { document.title = "Admin Panel | VR Classes"; }, []);
  if (user === null) return <div className="p-10 text-slate-500">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;
  const doLogout = async () => { await logout(); nav("/admin/login"); };
  const item = NAV.find((n) => n.path === tab);
  return (
    <div className="min-h-screen bg-slate-100 flex">
      <Toaster position="top-right" richColors />
      <aside className={`fixed lg:static top-0 bottom-0 left-0 z-30 w-64 bg-[#0F172A] text-white p-4 flex-col overflow-y-auto ${sidebarOpen ? "flex" : "hidden lg:flex"}`}>
        <div className="flex items-center justify-between mb-6">
          <div className="font-display font-extrabold text-lg">VR Admin</div>
          <button className="lg:hidden text-white/70" onClick={() => setSidebarOpen(false)}><X size={18} /></button>
        </div>
        {NAV.map((n) => (
          <button key={n.path} onClick={() => { setTab(n.path); setSidebarOpen(false); }} className={`text-left flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold mb-1 ${tab === n.path ? "bg-white/10" : "hover:bg-white/5"}`} data-testid={`admin-nav-${n.path}`}>
            <n.icon size={16} /> {n.label}
          </button>
        ))}
        <button onClick={doLogout} className="mt-auto flex items-center gap-2 px-3 py-2 rounded-md text-sm font-semibold bg-red-600 hover:bg-red-700" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/40 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <button className="lg:hidden h-9 w-9 border border-slate-200 rounded" onClick={() => setSidebarOpen(true)} data-testid="admin-sidebar-open">☰</button>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg truncate">{item?.label || "Dashboard"}</div>
            <div className="text-xs text-slate-500 truncate">{user.email}</div>
          </div>
          <Link to="/" className="text-sm text-[#1E3A8A] font-semibold whitespace-nowrap">View Site →</Link>
        </header>
        <main className="p-4 sm:p-6">
          {tab === "overview" && <Overview />}
          {tab === "homepage" && <HomepageEditor />}
          {tab === "statistics" && <StatisticsEditor />}
          {tab === "seo" && <SEOEditor />}
          {tab === "settings" && <SettingsView />}
          {tab === "orders" && <SimpleList endpoint="orders" columns={["name", "email", "phone", "amount_inr", "status", "created_at"]} />}
          {tab === "contacts" && <SimpleList endpoint="contacts" columns={["name", "email", "phone", "message", "created_at"]} />}
          {SECTION_MAP[tab] && <CmsCrud sectionKey={tab} />}
        </main>
      </div>
    </div>
  );
}

/* ================= OVERVIEW ================= */
function Overview() {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  const cards = [
    { k: "classes", l: "Classes" }, { k: "subjects", l: "Subjects" }, { k: "chapters", l: "Chapters" },
    { k: "notes", l: "Notes" }, { k: "mcqs", l: "MCQs" }, { k: "quizzes", l: "Quizzes" },
    { k: "tests", l: "Tests" }, { k: "blog_posts", l: "Blog Posts" }, { k: "videos", l: "Videos" },
    { k: "faqs", l: "FAQs" }, { k: "contacts", l: "Contacts" }, { k: "orders", l: "Course Orders" },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.k} className="vr-card p-5" data-testid={`stat-${c.k}`}>
            <div className="text-xs text-slate-500 uppercase">{c.l}</div>
            <div className="font-display font-black text-3xl text-[#1E3A8A] mt-1">{s[c.k] ?? "—"}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 vr-card p-5">
        <div className="font-display font-bold text-lg mb-2">Quick Guide</div>
        <ul className="text-sm text-slate-600 space-y-1 list-disc pl-5">
          <li>Add classes, subjects, chapters first — then notes / MCQs / quizzes will reference them.</li>
          <li>To add a YouTube video, just paste the video URL — the ID is auto-extracted.</li>
          <li>Homepage stats and hero text are editable in <b>Homepage Content</b> and <b>Statistics</b>.</li>
          <li>English Course ₹199 is editable in <b>Courses</b>.</li>
        </ul>
      </div>
    </div>
  );
}

/* ================= CMS CRUD ================= */
function CmsCrud({ sectionKey }) {
  const section = SECTION_MAP[sectionKey];
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} for new | doc for edit
  const [deleting, setDeleting] = useState(null); // row to delete
  const [query, setQuery] = useState("");
  const [bulkImport, setBulkImport] = useState(false);

  const load = () => { setLoading(true); api.get(`/admin/${sectionKey}`).then((r) => setRows(r.data)).finally(() => setLoading(false)); };
  useEffect(() => { setQuery(""); load(); /* eslint-disable-next-line */ }, [sectionKey]);

  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => JSON.stringify(r).toLowerCase().includes(q));
  }, [rows, query]);

  const openNew = () => setEditing(applyDefaults(section.fields, {}));
  const openEdit = (r) => setEditing({ ...r });
  const confirmDelete = async () => {
    try { await api.delete(`/admin/${sectionKey}/${deleting.id}`); toast.success("Deleted"); setDeleting(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <input placeholder="Filter..." value={query} onChange={(e) => setQuery(e.target.value)} className="h-10 px-3 border border-slate-200 rounded text-sm" data-testid="admin-filter" />
        <div className="text-xs text-slate-500">{filtered.length} of {rows.length}</div>
        {(sectionKey === "mcqs" || sectionKey === "pyq") && (
          <button
            type="button"
            onClick={() => setBulkImport(true)}
            className="ml-auto h-10 px-4 rounded border border-slate-200 text-sm font-semibold hover:bg-slate-50 inline-flex items-center gap-2"
            data-testid="admin-bulk-import-btn"
          >
            <Upload size={16} /> Bulk Import
          </button>
        )}
        <button onClick={openNew} className={`${(sectionKey === "mcqs" || sectionKey === "pyq") ? "" : "ml-auto"} vr-btn-primary h-10 px-4`} data-testid="admin-new-btn"><Plus size={16} /> Add New</button>
      </div>
      <div className="vr-card overflow-x-auto">
        <table className="w-full text-sm min-w-[600px]">
          <thead className="bg-slate-100">
            <tr>
              <th className="text-left p-3 font-semibold">{section.titleField}</th>
              <th className="text-left p-3 font-semibold">Slug/ID</th>
              <th className="text-left p-3 font-semibold">Status</th>
              <th className="p-3 w-36">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="p-6 text-slate-500 text-center">Loading...</td></tr>}
            {!loading && filtered.map((r) => {
              const title = r[section.titleField] || r.title || r.name || r.question;
              const published = r.published !== undefined ? r.published : r.active !== undefined ? r.active : true;
              return (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`row-${r.id}`}>
                  <td className="p-3 font-semibold text-[#0F172A] max-w-xs truncate" title={title}>{title}</td>
                  <td className="p-3 text-slate-500 text-xs font-mono">{r.slug || r.id?.slice(-6)}</td>
                  <td className="p-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${published ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{published ? "Published" : "Draft"}</span></td>
                  <td className="p-3 flex gap-1">
                    <button onClick={() => openEdit(r)} className="h-8 w-8 rounded border border-slate-200 hover:bg-blue-50 text-[#1E3A8A] flex items-center justify-center" title="Edit" data-testid={`edit-${r.id}`}><Pencil size={14} /></button>
                    <button onClick={() => setDeleting(r)} className="h-8 w-8 rounded border border-slate-200 hover:bg-red-50 text-red-600 flex items-center justify-center" title="Delete" data-testid={`del-${r.id}`}><Trash2 size={14} /></button>
                  </td>
                </tr>
              );
            })}
            {!loading && filtered.length === 0 && <tr><td colSpan={4} className="p-6 text-slate-500 text-center">No records.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <EditDialog
          section={section}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete "${deleting[section.titleField] || deleting.id}"?`}
          description="This cannot be undone. Any content pointing at this item may become orphaned."
          onCancel={() => setDeleting(null)}
          onConfirm={confirmDelete}
        />
      )}

      {bulkImport && sectionKey === "mcqs" && (
        <BulkImportCsv
          entity="mcqs"
          title="Bulk Import MCQs (CSV)"
          templateFilename="mcq-import-template.csv"
          columnsHelp={
            <>
              Columns: <code>class, subject, chapter, question, option_a, option_b, option_c, option_d, correct_answer, explanation</code>.
              Use existing <b>slugs</b> for class/subject/chapter. <code>correct_answer</code> must be one of <b>A</b>, <b>B</b>, <b>C</b>, <b>D</b>. Chapter is optional.
            </>
          }
          onClose={() => setBulkImport(false)}
          onImported={load}
        />
      )}

      {bulkImport && sectionKey === "pyq" && (
        <BulkImportCsv
          entity="pyq"
          title="Bulk Import Previous Year Questions (CSV)"
          templateFilename="pyq-import-template.csv"
          columnsHelp={
            <>
              Columns: <code>class, subject, chapter, year, question, answer, marks, pdf_url</code>.
              Use existing <b>slugs</b> for class/subject/chapter. <code>year</code> is required (e.g. 2024). Chapter, answer, marks and pdf_url are optional.
            </>
          }
          onClose={() => setBulkImport(false)}
          onImported={load}
        />
      )}
    </div>
  );
}

/* ================= EDIT DIALOG ================= */
function EditDialog({ section, initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial.id;

  const save = async (e) => {
    e.preventDefault();
    const errs = validateForm(section.fields, form);
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error("Please fix the highlighted fields");
    setSaving(true);
    try {
      const body = prepareSubmit(section.fields, form);
      if (isEdit) await api.put(`/admin/${section.key}/${initial.id}`, body);
      else await api.post(`/admin/${section.key}`, body);
      toast.success(isEdit ? "Updated" : "Created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto">
      <form onSubmit={save} className="bg-white w-full max-w-2xl rounded-lg shadow-xl my-8" data-testid="edit-dialog">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="font-display font-bold text-lg">{isEdit ? `Edit ${section.label}` : `New ${section.label}`}</div>
          <button type="button" onClick={onClose} className="h-8 w-8 rounded hover:bg-slate-100" data-testid="edit-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          {section.fields.map((f) => {
            const fieldWithBulk = { ...f, onFormChange: (patch) => setForm((prev) => ({ ...prev, ...patch })) };
            return (
              <div key={f.key}>
                {f.type !== "bool" && <label className="text-xs font-bold text-slate-600 uppercase tracking-wide">{f.label}{f.required && <span className="text-red-600"> *</span>}</label>}
                <div className="mt-1">
                  <FieldRenderer field={fieldWithBulk} value={form[f.key]} onChange={(v) => setForm({ ...form, [f.key]: v })} form={form} />
                </div>
                {errors[f.key] && <div className="text-xs text-red-600 mt-1" data-testid={`err-${f.key}`}>{errors[f.key]}</div>}
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50">
          <button type="button" onClick={onClose} className="h-10 px-4 rounded border border-slate-200" data-testid="edit-cancel">Cancel</button>
          <button disabled={saving} className="vr-btn-primary h-10 px-5" data-testid="edit-save"><Save size={16} /> {saving ? "Saving..." : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

/* ================= CONFIRM DIALOG ================= */
function ConfirmDialog({ title, description, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white w-full max-w-md rounded-lg shadow-xl p-6" data-testid="confirm-dialog">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 text-red-600 flex items-center justify-center"><AlertTriangle size={20} /></div>
          <div>
            <div className="font-display font-bold text-lg">{title}</div>
            <p className="text-sm text-slate-600 mt-1">{description}</p>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className="h-10 px-4 rounded border border-slate-200" data-testid="confirm-cancel">Cancel</button>
          <button onClick={onConfirm} className="h-10 px-4 rounded bg-red-600 text-white font-semibold hover:bg-red-700" data-testid="confirm-delete">Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ================= HOMEPAGE EDITOR ================= */
function HpField({ k, label, textarea, value, onChange }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-600 uppercase">{label}</label>
      {textarea
        ? <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full p-2 border border-slate-200 rounded text-sm" data-testid={`hp-${k}`} />
        : <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-10 px-3 border border-slate-200 rounded text-sm" data-testid={`hp-${k}`} />}
    </div>
  );
}

function HomepageEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/homepage").then((r) => setData(r.data)); }, []);
  if (!data) return <div>Loading...</div>;
  const setK = (k, v) => setData((prev) => ({ ...prev, [k]: v }));
  const setVis = (k, v) => setData((prev) => ({ ...prev, section_visibility: { ...(prev.section_visibility || {}), [k]: v } }));
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/homepage", data); toast.success("Homepage saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const visKeys = [
    ["popular_classes", "Popular Classes"],
    ["bihar_2027", "Bihar Board 2027"],
    ["popular_subjects", "Popular Subjects"],
    ["latest_notes", "Latest Notes"],
    ["mcq_quizzes", "MCQ Quizzes"],
    ["youtube_classes", "YouTube Classes"],
    ["course_cta", "Course CTA"],
    ["latest_blog", "Latest Blog"],
    ["faqs", "FAQs"],
  ];
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="vr-card p-5 space-y-4">
        <div className="font-display font-bold text-lg">Hero Section</div>
        <HpField k="hero_badge_text" label="Badge Text" value={data.hero_badge_text} onChange={(v) => setK("hero_badge_text", v)} />
        <HpField k="hero_hindi_heading_line1" label="Heading Line 1 (Hindi)" value={data.hero_hindi_heading_line1} onChange={(v) => setK("hero_hindi_heading_line1", v)} />
        <HpField k="hero_hindi_heading_line2" label="Heading Line 2 (Hindi)" value={data.hero_hindi_heading_line2} onChange={(v) => setK("hero_hindi_heading_line2", v)} />
        <HpField k="hero_description" label="Description" textarea value={data.hero_description} onChange={(v) => setK("hero_description", v)} />
        <div>
          <label className="text-xs font-bold text-slate-600 uppercase">Hero Image</label>
          <div className="mt-1">
            <FileDropzone kind="image" value={data.hero_image || ""} onChange={(v) => setK("hero_image", v)} testid="hp-hero_image" />
          </div>
        </div>
      </div>
      <div className="vr-card p-5 space-y-4">
        <div className="font-display font-bold text-lg">CTA Buttons</div>
        <div className="grid grid-cols-2 gap-3">
          <HpField k="cta_notes_label" label="Notes Label" value={data.cta_notes_label} onChange={(v) => setK("cta_notes_label", v)} />
          <HpField k="cta_notes_link" label="Notes Link" value={data.cta_notes_link} onChange={(v) => setK("cta_notes_link", v)} />
          <HpField k="cta_quiz_label" label="Quiz Label" value={data.cta_quiz_label} onChange={(v) => setK("cta_quiz_label", v)} />
          <HpField k="cta_quiz_link" label="Quiz Link" value={data.cta_quiz_link} onChange={(v) => setK("cta_quiz_link", v)} />
          <HpField k="cta_youtube_label" label="YouTube Label" value={data.cta_youtube_label} onChange={(v) => setK("cta_youtube_label", v)} />
          <HpField k="cta_youtube_link" label="YouTube Link (blank = channel)" value={data.cta_youtube_link} onChange={(v) => setK("cta_youtube_link", v)} />
          <HpField k="cta_course_label" label="Course Label" value={data.cta_course_label} onChange={(v) => setK("cta_course_label", v)} />
          <HpField k="cta_course_link" label="Course Link" value={data.cta_course_link} onChange={(v) => setK("cta_course_link", v)} />
        </div>
      </div>
      <div className="vr-card p-5 space-y-4">
        <div className="font-display font-bold text-lg">Bihar Board 2027 Section</div>
        <HpField k="bihar_2027_subheading" label="Small Subheading (red)" value={data.bihar_2027_subheading} onChange={(v) => setK("bihar_2027_subheading", v)} />
        <HpField k="bihar_2027_heading" label="Section Heading" value={data.bihar_2027_heading} onChange={(v) => setK("bihar_2027_heading", v)} />
      </div>
      <div className="vr-card p-5">
        <div className="font-display font-bold text-lg mb-2">Section Visibility</div>
        <div className="grid grid-cols-2 gap-2 text-sm">
          {visKeys.map(([k, l]) => (
            <label key={k} className="inline-flex items-center gap-2">
              <input type="checkbox" checked={!!(data.section_visibility?.[k] ?? true)} onChange={(e) => setVis(k, e.target.checked)} data-testid={`vis-${k}`} /> {l}
            </label>
          ))}
        </div>
      </div>
      <div className="lg:col-span-2 flex justify-end">
        <button disabled={saving} onClick={save} className="vr-btn-primary h-11 px-6" data-testid="hp-save"><Save size={16} /> {saving ? "Saving..." : "Save Homepage"}</button>
      </div>
    </div>
  );
}

/* ================= STATISTICS EDITOR ================= */
function StatisticsEditor() {
  const [data, setData] = useState(null);
  const [live, setLive] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    Promise.all([api.get("/admin/homepage"), api.get("/admin/stats/live")]).then(([h, l]) => {
      setData(h.data); setLive(l.data);
    });
  }, []);
  if (!data) return <div>Loading...</div>;
  const stats = data.stats || [];
  const setStats = (arr) => setData({ ...data, stats: arr });
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/homepage", { stats: data.stats }); toast.success("Statistics saved"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  return (
    <div>
      <div className="vr-card p-4 mb-4 text-sm">
        <div className="font-semibold">Live database counts (for reference)</div>
        <div className="mt-2 flex flex-wrap gap-2">
          {Object.entries(live).map(([k, v]) => (
            <span key={k} className="vr-chip">{k}: <b className="ml-1">{v}</b></span>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-2">You control the exact values shown on the homepage below — nothing is auto-fabricated.</p>
      </div>
      <div className="space-y-3">
        {stats.map((s, i) => (
          <div key={i} className="vr-card p-4 grid grid-cols-1 md:grid-cols-5 gap-2 items-center" data-testid={`stat-row-${i}`}>
            <input placeholder="Label (e.g. Notes)" value={s.label || ""} onChange={(e) => setStats(stats.map((r, j) => j === i ? { ...r, label: e.target.value } : r))} className="h-10 px-3 border border-slate-200 rounded text-sm" />
            <input placeholder="Value (e.g. 500+)" value={s.value || ""} onChange={(e) => setStats(stats.map((r, j) => j === i ? { ...r, value: e.target.value } : r))} className="h-10 px-3 border border-slate-200 rounded text-sm" />
            <input placeholder="Lucide icon (BookOpen)" value={s.icon || ""} onChange={(e) => setStats(stats.map((r, j) => j === i ? { ...r, icon: e.target.value } : r))} className="h-10 px-3 border border-slate-200 rounded text-sm" />
            <input type="number" placeholder="Order" value={s.order ?? 0} onChange={(e) => setStats(stats.map((r, j) => j === i ? { ...r, order: Number(e.target.value) } : r))} className="h-10 px-3 border border-slate-200 rounded text-sm" />
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1 text-sm"><input type="checkbox" checked={s.active !== false} onChange={(e) => setStats(stats.map((r, j) => j === i ? { ...r, active: e.target.checked } : r))} /> Active</label>
              <button type="button" onClick={() => setStats(stats.filter((_, j) => j !== i))} className="ml-auto h-9 w-9 rounded border border-slate-200 text-red-600"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-3 flex justify-between gap-2">
        <button onClick={() => setStats([...stats, { label: "", value: "", icon: "BookOpen", order: stats.length + 1, active: true }])} className="h-10 px-4 rounded border border-slate-200 text-sm font-semibold" data-testid="stat-add"><Plus size={14} className="inline" /> Add Stat</button>
        <button disabled={saving} onClick={save} className="vr-btn-primary h-10 px-5" data-testid="stat-save"><Save size={16} /> {saving ? "Saving..." : "Save Statistics"}</button>
      </div>
    </div>
  );
}

/* ================= SEO EDITOR ================= */
function SeoField({ k, label, textarea, value, onChange }) {
  return (
    <div>
      <label className="text-xs font-bold text-slate-600 uppercase">{label}</label>
      {textarea
        ? <textarea rows={3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full p-2 border border-slate-200 rounded text-sm" data-testid={`seo-${k}`} />
        : <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="mt-1 w-full h-10 px-3 border border-slate-200 rounded text-sm" data-testid={`seo-${k}`} />}
    </div>
  );
}

function SEOEditor() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data || {})); }, []);
  if (!s) return <div>Loading...</div>;
  const setK = (k, v) => setS((prev) => ({ ...prev, [k]: v }));
  const save = async () => {
    try { await api.put("/admin/settings", s); toast.success("SEO saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="max-w-2xl vr-card p-5 space-y-4">
      <div className="font-display font-bold text-lg">Global SEO & Analytics</div>
      <SeoField k="site_title" label="Site Title" value={s.site_title} onChange={(v) => setK("site_title", v)} />
      <SeoField k="tagline" label="Default Meta Description / Tagline" textarea value={s.tagline} onChange={(v) => setK("tagline", v)} />
      <SeoField k="ga_measurement_id" label="Google Analytics Measurement ID (e.g. G-XXXXXXX)" value={s.ga_measurement_id} onChange={(v) => setK("ga_measurement_id", v)} />
      <div className="text-xs text-slate-500">Per-page SEO (title/description) is edited on each individual Note, Blog Post, and Class in their own CMS section.</div>
      <div className="flex justify-end"><button onClick={save} className="vr-btn-primary" data-testid="seo-save"><Save size={16} /> Save SEO</button></div>
    </div>
  );
}

/* ================= SITE SETTINGS ================= */
function SettingsView() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data || {})); }, []);
  if (!s) return <div>Loading...</div>;
  const setK = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    try { await api.put("/admin/settings", { ...s, course_price_inr: Number(s.course_price_inr) || 199 }); toast.success("Settings saved"); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const fields = [
    ["site_title", "Site Title"], ["tagline", "Tagline"],
    ["youtube_channel_url", "YouTube Channel URL"],
    ["facebook_url", "Facebook"], ["instagram_url", "Instagram"], ["telegram_url", "Telegram"], ["whatsapp_url", "WhatsApp"],
    ["contact_email", "Contact Email"], ["contact_phone", "Contact Phone"],
    ["course_price_inr", "Course Price (INR)"],
  ];
  return (
    <div className="max-w-2xl vr-card p-5 space-y-3">
      {fields.map(([k, l]) => (
        <div key={k}>
          <label className="text-xs text-slate-500 font-semibold uppercase">{l}</label>
          <input value={s[k] ?? ""} onChange={(e) => setK(k, e.target.value)} className="mt-1 w-full h-10 px-3 border border-slate-200 rounded text-sm" data-testid={`set-${k}`} />
        </div>
      ))}
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!s.ads_enabled} onChange={(e) => setK("ads_enabled", e.target.checked)} data-testid="set-ads_enabled" /> Show AdSense placeholder slots on Home, Note reader, Blog reader</label>
      <button onClick={save} className="vr-btn-primary" data-testid="settings-save"><Save size={16} /> Save Settings</button>
    </div>
  );
}

/* ================= SIMPLE READ-ONLY LIST ================= */
function SimpleList({ endpoint, columns }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get(`/admin/${endpoint}`).then((r) => setRows(r.data)); }, [endpoint]);
  return (
    <div className="vr-card overflow-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-100"><tr>{columns.map((c) => <th key={c} className="text-left p-3 font-semibold">{c}</th>)}</tr></thead>
        <tbody>{rows.map((r) => <tr key={r.id} className="border-t border-slate-100">{columns.map((c) => <td key={c} className="p-3 text-slate-700 align-top max-w-xs truncate">{String(r[c] ?? "").slice(0, 200)}</td>)}</tr>)}</tbody>
      </table>
      {rows.length === 0 && <div className="p-6 text-slate-500 text-sm">No records.</div>}
    </div>
  );
}
